import React, { useEffect, useMemo, useReducer, useState } from "react";
import { FaExpand, FaStore } from "react-icons/fa";
import logoFallback from "../../assets/images/logo.png";
import {
  createCustomerDisplayChannel,
  DISPLAY_EVENTS,
  readCachedDisplayState,
} from "../../services/customerDisplayChannel";
import { initialCustomerDisplayState, reduceCustomerDisplayEvent } from "./customerDisplayState";
import "./CustomerDisplay.css";

const CustomerDisplay = () => {
  const cached = readCachedDisplayState();
  const [state, dispatch] = useReducer(reduceCustomerDisplayEvent, cached || initialCustomerDisplayState);
  const [slide, setSlide] = useState(0);
  const formatter = useMemo(() => new Intl.NumberFormat(state.display.locale, {
    style: "currency",
    currency: state.display.currency,
    minimumFractionDigits: 2,
  }), [state.display.currency, state.display.locale]);

  useEffect(() => {
    const transport = createCustomerDisplayChannel({ onMessage: dispatch });
    transport.publish(DISPLAY_EVENTS.DISPLAY_STATE_REQUESTED);
    return () => transport.close();
  }, []);

  useEffect(() => {
    if (state.display.promotionImages.length < 2) return undefined;
    const timer = setInterval(
      () => setSlide((value) => (value + 1) % state.display.promotionImages.length),
      6000
    );
    return () => clearInterval(timer);
  }, [state.display.promotionImages.length]);

  useEffect(() => {
    if (state.mode !== "COMPLETED") return undefined;
    const timer = setTimeout(
      () => dispatch({ type: DISPLAY_EVENTS.CART_CLEARED }),
      state.display.confirmationSeconds * 1000
    );
    return () => clearTimeout(timer);
  }, [state.display.confirmationSeconds, state.mode, state.updatedAt]);

  const requestFullscreen = () => document.documentElement.requestFullscreen?.();
  const isWelcome = state.mode === "WELCOME" || state.lines.length === 0 && state.mode !== "COMPLETED";

  return (
    <main className={`customer-display customer-display--${state.mode.toLowerCase()}`}>
      <button className="customer-display__fullscreen" onClick={requestFullscreen} title="Plein écran">
        <FaExpand />
      </button>

      {isWelcome ? (
        <section className="customer-welcome">
          <img className="customer-welcome__logo" src={state.shop.logo || logoFallback} alt="" />
          <h1>{state.shop.name}</h1>
          <p className="customer-welcome__message">{state.display.welcomeMessage}</p>
          {state.display.promotionImages.length > 0 && (
            <img
              className="customer-welcome__promotion"
              src={state.display.promotionImages[slide]}
              alt="Promotion"
            />
          )}
          {state.display.qrCode && <img className="customer-welcome__qr" src={state.display.qrCode} alt="QR code" />}
          <div className="customer-welcome__contact">
            {[state.shop.address, state.shop.phone, state.shop.email].filter(Boolean).join(" • ")}
          </div>
        </section>
      ) : state.mode === "COMPLETED" ? (
        <section className="customer-completed">
          <div className="customer-completed__icon">✓</div>
          <h1>Merci pour votre achat</h1>
          <div className="customer-completed__amount">{formatter.format(state.totals.finalAmount)}</div>
          <div className="customer-completed__details">
            <span>Montant reçu <strong>{formatter.format(state.payment.receivedAmount)}</strong></span>
            <span>Monnaie à rendre <strong>{formatter.format(state.payment.changeAmount)}</strong></span>
          </div>
        </section>
      ) : (
        <section className="customer-sale">
          <header className="customer-sale__header">
            <div className="customer-sale__brand">
              {state.shop.logo ? <img src={state.shop.logo} alt="" /> : <FaStore />}
              <strong>{state.shop.name}</strong>
            </div>
            <span>Votre panier</span>
          </header>
          <div className="customer-sale__table-wrap">
            <table className="customer-sale__table">
              <thead><tr><th>Produit</th><th>Qté</th><th>Prix unitaire</th><th>Remise</th><th>Total</th></tr></thead>
              <tbody>
                {state.lines.map((line) => (
                  <tr key={line.id} className={line.id === state.highlightedLineId ? "is-highlighted" : ""}>
                    <td>{line.designation}</td>
                    <td>{line.quantity}</td>
                    <td>{formatter.format(line.unitPrice)}</td>
                    <td>{line.discount > 0 ? formatter.format(line.discount) : "—"}</td>
                    <td>{formatter.format(line.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="customer-sale__summary">
            <div><span>Sous-total</span><strong>{formatter.format(state.totals.subtotal)}</strong></div>
            {state.totals.discount > 0 && <div><span>Remise totale</span><strong>- {formatter.format(state.totals.discount)}</strong></div>}
            <div className="customer-sale__total"><span>À payer</span><strong>{formatter.format(state.totals.finalAmount)}</strong></div>
            {state.mode === "PAYMENT" && (
              <div className="customer-sale__payment">
                <span>Payé <strong>{formatter.format(state.payment.paidAmount)}</strong></span>
                <span>Reste <strong>{formatter.format(state.payment.remainingAmount)}</strong></span>
              </div>
            )}
          </aside>
        </section>
      )}
    </main>
  );
};

export default CustomerDisplay;
